<template>
  <main class="adm-page">

    <!-- ── Header ──────────────────────────────────── -->
    <div class="adm-header">
      <div>
        <h1 class="adm-header__title">Gestion des Articles</h1>
        <p class="adm-header__sub">{{ articlesAdminPagination.total }} articles au total</p>
      </div>
      <div class="adm-header__actions">
        <div style="position:relative">
          <button class="adm-btn adm-btn--outline" @click="toggleExportMenu">
            <Download />Exporter
          </button>
          <div v-if="showExportMenu" class="adm-drop__menu" style="top:calc(100% + .35rem);right:0;z-index:50">
            <button class="adm-drop__item" @click="exportToExcel"><FileSpreadsheet />Export Excel</button>
            <button class="adm-drop__item" @click="exportToPDF"><FileText />Export PDF</button>
          </div>
        </div>
        <button class="adm-btn adm-btn--terra" @click="openNewArticleDialog"><Plus />Nouvel article</button>
      </div>
    </div>

    <!-- ── Filtres ──────────────────────────────────── -->
    <div class="adm-filters">
      <div class="adm-search" style="flex:1;min-width:200px">
        <Search />
        <input v-model="filters.search" @input="debouncedSearch" placeholder="Rechercher un article…" class="adm-input" />
      </div>
      <select v-model="filters.status" @change="applyFilters" class="adm-input adm-input--sm">
        <option value="">Tous les statuts</option>
        <option value="published">Publié</option>
        <option value="draft">Brouillon</option>
        <option value="archived">Archivé</option>
      </select>
      <select v-model="filters.category" @change="applyFilters" class="adm-input adm-input--sm">
        <option value="">Toutes les catégories</option>
        <option value="Actualité">Actualité</option>
        <option value="Formation">Formation</option>
        <option value="Partenariat">Partenariat</option>
        <option value="Rapport">Rapport</option>
        <option value="Témoignage">Témoignage</option>
      </select>
      <button class="adm-btn adm-btn--outline adm-btn--sm" @click="resetFilters"><X />Réinitialiser</button>
    </div>

    <!-- ── Table ────────────────────────────────────── -->
    <div class="adm-card">
      <div v-if="loading" class="adm-loading"><span class="adm-ring"></span></div>
      <div v-else>
        <div class="adm-table-wrap">
          <table class="adm-table">
            <thead>
              <tr>
                <th><button @click="sortBy('title')">Titre <ChevronUp v-if="articlesAdminFilters.sort_by==='title' && articlesAdminFilters.sort_order==='asc'" /><ChevronDown v-else-if="articlesAdminFilters.sort_by==='title'" /></button></th>
                <th>Image</th>
                <th><button @click="sortBy('category')">Catégorie <ChevronUp v-if="articlesAdminFilters.sort_by==='category' && articlesAdminFilters.sort_order==='asc'" /><ChevronDown v-else-if="articlesAdminFilters.sort_by==='category'" /></button></th>
                <th>Auteur</th>
                <th><button @click="sortBy('status')">Statut</button></th>
                <th><button @click="sortBy('views_count')">Vues</button></th>
                <th><button @click="sortBy('published_at')">Date</button></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!filteredArticlesAdmin.length">
                <td colspan="8" class="adm-table__empty">Aucun article trouvé</td>
              </tr>
              <tr v-for="article in filteredArticlesAdmin" :key="article.id">
                <td data-label="Titre">
                  <div class="adm-cell-title">{{ article.title }}</div>
                  <div class="adm-cell-sub" v-if="article.is_featured">⭐ À la une</div>
                </td>
                <td data-label="Image" class="no-label">
                  <div class="adm-thumb">
                    <img v-if="article.featured_image" :src="imageUrl + article.featured_image" :alt="article.title" />
                    <Image v-else />
                  </div>
                </td>
                <td data-label="Catégorie"><span class="adm-chip">{{ article.category }}</span></td>
                <td data-label="Auteur">{{ article.author?.name || '—' }}</td>
                <td data-label="Statut">
                  <span class="adm-badge" :class="{
                    'adm-badge--green': article.status==='published',
                    'adm-badge--amber': article.status==='draft',
                    'adm-badge--gray':  article.status==='archived'
                  }">{{ getStatusLabel(article.status) }}</span>
                </td>
                <td data-label="Vues" class="adm-cell-num">{{ formatNumber(article.views_count) }}</td>
                <td data-label="Date" class="adm-cell-date">{{ formatDate(article.published_at || article.created_at) }}</td>
                <td data-label="Actions" class="no-label">
                  <div class="adm-drop">
                    <button class="adm-btn adm-btn--outline adm-btn--xs" @click.stop="toggleActionsMenu(article.id)">
                      <MoreVertical />
                    </button>
                    <div v-if="activeActionMenu === article.id" class="adm-drop__menu">
                      <button class="adm-drop__item" @click="previewArticle(article)"><Eye />Prévisualiser</button>
                      <button class="adm-drop__item" @click="togglePublishStatus(article)">
                        <EyeOff v-if="article.status==='published'" /><Eye v-else />
                        {{ article.status==='published' ? 'Dépublier' : 'Publier' }}
                      </button>
                      <button class="adm-drop__item" @click="editArticle(article)"><Edit />Modifier</button>
                      <button class="adm-drop__item adm-drop__item--red" @click="confirmDelete(article)"><Trash2 />Supprimer</button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="articlesAdminPagination.last_page > 1" class="adm-pagination">
          <p class="adm-pagination__info">
            {{ (articlesAdminPagination.current_page-1)*articlesAdminPagination.per_page+1 }}–{{ Math.min(articlesAdminPagination.current_page*articlesAdminPagination.per_page,articlesAdminPagination.total) }} sur {{ articlesAdminPagination.total }}
          </p>
          <div class="adm-pagination__pages">
            <button class="adm-page-btn" :disabled="articlesAdminPagination.current_page===1" @click="changePage(articlesAdminPagination.current_page-1)"><ChevronLeft /></button>
            <button v-for="p in displayedPages" :key="p" class="adm-page-btn" :class="{'adm-page-btn--active':articlesAdminPagination.current_page===p}" @click="changePage(p)">{{ p }}</button>
            <button class="adm-page-btn" :disabled="articlesAdminPagination.current_page===articlesAdminPagination.last_page" @click="changePage(articlesAdminPagination.current_page+1)"><ChevronRight /></button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════
         MODAL : Formulaire article
    ══════════════════════════════════════════ -->
    <Transition name="adm-modal">
      <div v-if="showDialog" class="adm-modal" @click.self="closeDialog">
        <div class="adm-modal__box adm-modal__box--xl">
          <div class="adm-modal__head">
            <h3 class="adm-modal__title">{{ editingArticle ? "Modifier l'article" : "Nouvel article" }}</h3>
            <button class="adm-modal__close" @click="closeDialog"><X /></button>
          </div>
          <form @submit.prevent="submitForm" class="adm-modal__body">

            <div class="adm-field">
              <label class="adm-label">Titre <span>*</span></label>
              <input v-model="form.title" required class="adm-input" placeholder="Entrez le titre" />
            </div>

            <div class="adm-field">
              <label class="adm-label">Slug</label>
              <div style="display:flex;gap:.5rem">
                <input v-model="form.slug" class="adm-input" placeholder="slug-de-l-article" style="flex:1" />
                <button type="button" @click="generateSlug" class="adm-btn adm-btn--outline adm-btn--sm">Générer</button>
              </div>
              <p class="adm-hint">Laissez vide pour générer automatiquement</p>
            </div>

            <div class="adm-form-grid">
              <div class="adm-field">
                <label class="adm-label">Catégorie <span>*</span></label>
                <select v-model="form.category" required class="adm-input">
                  <option value="">Sélectionner…</option>
                  <option value="Actualité">Actualité</option>
                  <option value="Formation">Formation</option>
                  <option value="Partenariat">Partenariat</option>
                  <option value="Rapport">Rapport</option>
                  <option value="Témoignage">Témoignage</option>
                </select>
              </div>
              <div class="adm-field">
                <label class="adm-label">Statut</label>
                <select v-model="form.status" class="adm-input">
                  <option value="draft">Brouillon</option>
                  <option value="published">Publié</option>
                  <option value="archived">Archivé</option>
                </select>
              </div>
              <div class="adm-field">
                <label class="adm-label">Date de publication</label>
                <input v-model="form.published_at" type="datetime-local" class="adm-input" />
              </div>
              <div class="adm-field">
                <label class="adm-label">Temps de lecture (min)</label>
                <input v-model="form.reading_time_minutes" type="number" min="1" class="adm-input" placeholder="5" />
              </div>
            </div>

            <!-- Tags -->
            <div class="adm-field">
              <label class="adm-label">Tags</label>
              <div class="adm-tags">
                <span v-for="(tag,i) in (Array.isArray(form.tags)?form.tags:JSON.parse(form.tags||'[]'))" :key="i" class="adm-tag">
                  {{ tag }}
                  <button type="button" @click="removeTag(i)"><X /></button>
                </span>
              </div>
              <div style="display:flex;gap:.5rem">
                <input v-model="newTag" @keyup.enter="addTag" class="adm-input" placeholder="Ajouter un tag" style="flex:1" />
                <button type="button" @click="addTag" class="adm-btn adm-btn--outline adm-btn--sm"><Plus />Ajouter</button>
              </div>
            </div>

            <!-- Image à la une -->
            <div class="adm-field">
              <label class="adm-label">Image à la une</label>
              <input ref="featuredImageInput" type="file" accept="image/*" @change="handleFeaturedImageUpload" style="display:none" />
              <div v-if="form.featured_image_preview || form.featured_image" class="adm-upload-preview">
                <img :src="form.featured_image_preview || imageUrl+form.featured_image" />
                <button type="button" @click="removeFeaturedImage" class="adm-upload-remove"><X /></button>
              </div>
              <div v-else class="adm-upload" @click="$refs.featuredImageInput.click()">
                <Upload />
                <p>Cliquez ou glissez-déposez une image</p>
              </div>
            </div>

            <!-- Galerie -->
            <div class="adm-field">
              <label class="adm-label">Galerie d'images</label>
              <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(100px,1fr));gap:.5rem;margin-bottom:.75rem">
                <div v-for="(img,i) in form.gallery_images" :key="i" class="adm-upload-preview" style="display:inline-block">
                  <img :src="img.preview || imageUrl+img.path" style="width:100%;height:80px;object-fit:cover;border-radius:4px" />
                  <button type="button" @click="removeGalleryImage(i)" class="adm-upload-remove"><X /></button>
                </div>
              </div>
              <input ref="galleryInput" type="file" accept="image/*" multiple @change="handleGalleryUpload" style="display:none" />
              <button type="button" @click="$refs.galleryInput.click()" class="adm-btn adm-btn--outline adm-btn--sm adm-btn--full"><Plus />Ajouter des images</button>
            </div>

            <!-- Contenu -->
            <div class="adm-field">
              <label class="adm-label">Contenu <span>*</span></label>
              <editor v-model="form.content"></editor>
            </div>

            <!-- Extrait -->
            <div class="adm-field">
              <label class="adm-label">Extrait</label>
              <editor v-model="form.excerpt"></editor>
              <p class="adm-hint">Laissez vide pour générer automatiquement</p>
            </div>

            <!-- SEO -->
            <div style="border-top:1px solid var(--ink-12);padding-top:1.25rem">
              <p class="adm-section-title">Optimisation SEO</p>
              <div class="adm-form-grid">
                <div class="adm-field">
                  <label class="adm-label">Meta Title</label>
                  <input v-model="form.meta_title" class="adm-input" placeholder="Titre pour les moteurs de recherche" />
                </div>
                <div class="adm-field">
                  <label class="adm-label">Meta Description</label>
                  <textarea v-model="form.meta_description" rows="2" class="adm-input adm-textarea" placeholder="Description…"></textarea>
                </div>
              </div>
            </div>

          </form>
          <div class="adm-modal__foot">
            <button type="button" class="adm-btn adm-btn--outline" @click="closeDialog">Annuler</button>
            <button type="button" class="adm-btn adm-btn--terra" :disabled="loading" @click="submitForm">
              <span v-if="loading" class="adm-ring adm-ring--sm"></span>
              {{ editingArticle ? 'Modifier' : 'Créer' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- ── Delete confirm ───────────────────────────── -->
    <Transition name="adm-modal">
      <div v-if="showDeleteConfirm" class="adm-modal" @click.self="showDeleteConfirm=false">
        <div class="adm-modal__box">
          <div class="adm-modal__head">
            <h3 class="adm-modal__title">Confirmer la suppression</h3>
            <button class="adm-modal__close" @click="showDeleteConfirm=false"><X /></button>
          </div>
          <div class="adm-modal__body">
            <div class="adm-confirm">
              <div class="adm-confirm__icon"><Trash2 /></div>
              <h3>Supprimer l'article ?</h3>
              <p>« {{ articleToDelete?.title }} » sera définitivement supprimé. Cette action est irréversible.</p>
            </div>
          </div>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="showDeleteConfirm=false">Annuler</button>
            <button class="adm-btn adm-btn--red" @click="deleteArticle"><Trash2 />Supprimer</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Toast -->
    <Transition name="adm-toast">
      <div v-if="toastMessage" class="adm-toast">
        <CheckCircle />{{ toastMessage }}
      </div>
    </Transition>

  </main>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import editor from './editor.vue'
import {
  Download, FileSpreadsheet, FileText, Plus, Search, X,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  MoreVertical, Eye, EyeOff, Edit, Trash2, Image, Upload,
  AlertTriangle, CheckCircle
} from 'lucide-vue-next'
import { debounce } from 'lodash'

export default {
  name: 'ArticlesManagement',
  components: {
    Download, FileSpreadsheet, FileText, Plus, Search, X, editor,
    ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
    MoreVertical, Eye, EyeOff, Edit, Trash2, Image, Upload,
    AlertTriangle, CheckCircle
  },
  data() {
    return {
      activeActionMenu: null, showExportMenu: false,
      showDialog: false, showDeleteConfirm: false,
      editingArticle: null, articleToDelete: null, newTag: '',
      form: {
        id:null,title:'',slug:'',category:'',status:'draft',
        content:'',excerpt:'',featured_image:null,featured_image_preview:null,
        gallery_images:[],tags:[],meta_title:'',meta_description:'',published_at:'',
        reading_time_minutes:''
      },
      displayedPages:[],
      filters:{search:'',status:'',category:''}
    }
  },
  computed: {
    ...mapState({
      articlesAdmin:s=>s.articlesAdmin,articlesAdminPagination:s=>s.articlesAdminPagination,
      articlesAdminFilters:s=>s.articlesAdminFilters,loading:s=>s.loading,
      toastMessage:s=>s.toastMessage,imageUrl:s=>s.imageUrl
    }),
    ...mapGetters(['filteredArticlesAdmin'])
  },
  watch: {
    'articlesAdminPagination.last_page':{handler(){this.updateDisplayedPages()},immediate:true},
    'articlesAdminPagination.current_page':{handler(){this.updateDisplayedPages()}},
    'articlesAdminFilters.search':{handler(v){this.filters.search=v},immediate:true},
    'articlesAdminFilters.status':{handler(v){this.filters.status=v},immediate:true},
    'articlesAdminFilters.category':{handler(v){this.filters.category=v},immediate:true},
    toastMessage:{handler(v){if(v)setTimeout(()=>this.$store.commit('CLEAR_TOAST'),3000)}}
  },
  created() {
    this.fetchArticlesAdmin()
    this.debouncedSearch = debounce(this.applyFilters,500)
  },
  methods: {
    ...mapActions(['fetchArticlesAdmin','fetchArticleAdmin','saveArticleAdmin','deleteArticleAdmin',
      'toggleArticleStatusAdmin','setArticlesAdminFilter','setArticlesAdminPage','resetArticlesAdminFilters','uploadArticleImage']),
    openNewArticleDialog(){this.editingArticle=null;this.resetForm();this.showDialog=true},
    editArticle(a){
      this.editingArticle=a
      this.form={id:a.id,title:a.title,slug:a.slug,category:a.category,status:a.status,content:a.content,
        excerpt:a.excerpt||'',featured_image:a.featured_image,featured_image_preview:null,
        gallery_images:a.gallery_images||[],tags:a.tags||[],meta_title:a.meta_title||'',
        meta_description:a.meta_description||'',published_at:a.published_at?a.published_at.slice(0,16):'',
        reading_time_minutes:a.reading_time_minutes||''}
      this.showDialog=true;this.activeActionMenu=null
    },
    closeDialog(){this.showDialog=false;this.editingArticle=null;this.resetForm()},
    resetForm(){this.form={id:null,title:'',slug:'',category:'',status:'draft',content:'',excerpt:'',
      featured_image:null,featured_image_preview:null,gallery_images:[],tags:[],meta_title:'',
      meta_description:'',published_at:'',reading_time_minutes:''};this.newTag=''},
    generateSlug(){if(!this.form.slug&&this.form.title)this.form.slug=this.form.title.toLowerCase().replace(/[^\w\s-]/g,'').replace(/[\s_-]+/g,'-').replace(/^-+|-+$/g,'')},
    addTag(){if(this.newTag&&!this.form.tags.includes(this.newTag)){this.form.tags.push(this.newTag);this.newTag=''}},
    removeTag(i){this.form.tags.splice(i,1)},
    async handleFeaturedImageUpload(e){const f=e.target.files[0];if(f){this.form.featured_image=f;this.form.featured_image_preview=URL.createObjectURL(f)}},
    removeFeaturedImage(){this.form.featured_image=null;this.form.featured_image_preview=null;if(this.$refs.featuredImageInput)this.$refs.featuredImageInput.value=''},
    handleGalleryUpload(e){Array.from(e.target.files).forEach(f=>this.form.gallery_images.push({file:f,preview:URL.createObjectURL(f),path:null}))},
    removeGalleryImage(i){this.form.gallery_images.splice(i,1)},
    async submitForm(){
      try{
        const fd=new FormData()
        if(this.form.id)fd.append('id',this.form.id)
        fd.append('title',this.form.title);fd.append('slug',this.form.slug||'')
        fd.append('category',this.form.category);fd.append('status',this.form.status)
        fd.append('content',this.form.content)
        if(this.form.excerpt)fd.append('excerpt',this.form.excerpt)
        if(this.form.published_at)fd.append('published_at',this.form.published_at)
        if(this.form.reading_time_minutes)fd.append('reading_time_minutes',this.form.reading_time_minutes)
        fd.append('tags',JSON.stringify(this.form.tags))
        if(this.form.meta_title)fd.append('meta_title',this.form.meta_title)
        if(this.form.meta_description)fd.append('meta_description',this.form.meta_description)
        if(this.form.featured_image instanceof File)fd.append('featured_image',this.form.featured_image)
        this.form.gallery_images.forEach((img,i)=>{if(img.file)fd.append(`gallery_images[${i}]`,img.file)})
        await this.saveArticleAdmin(fd);this.closeDialog();this.fetchArticlesAdmin()
      }catch(e){console.error(e)}
    },
    async togglePublishStatus(a){try{await this.toggleArticleStatusAdmin({id:a.id,status:a.status});this.activeActionMenu=null;this.fetchArticlesAdmin()}catch(e){console.error(e)}},
    confirmDelete(a){this.articleToDelete=a;this.showDeleteConfirm=true;this.activeActionMenu=null},
    async deleteArticle(){if(this.articleToDelete){try{await this.deleteArticleAdmin(this.articleToDelete.id);this.showDeleteConfirm=false;this.articleToDelete=null;this.fetchArticlesAdmin()}catch(e){console.error(e)}}},
    previewArticle(a){window.open(`/articles/${a.slug}`,'_blank');this.activeActionMenu=null},
    applyFilters(){this.setArticlesAdminFilter({key:'search',value:this.filters.search});this.setArticlesAdminFilter({key:'status',value:this.filters.status});this.setArticlesAdminFilter({key:'category',value:this.filters.category});this.fetchArticlesAdmin()},
    sortBy(field){let order=this.articlesAdminFilters.sort_by===field?(this.articlesAdminFilters.sort_order==='asc'?'desc':'asc'):'asc';this.setArticlesAdminFilter({key:'sort_by',value:field});this.setArticlesAdminFilter({key:'sort_order',value:order});this.fetchArticlesAdmin()},
    resetFilters(){this.resetArticlesAdminFilters();this.filters={search:'',status:'',category:''};this.fetchArticlesAdmin()},
    changePage(p){if(p>=1&&p<=this.articlesAdminPagination.last_page)this.setArticlesAdminPage(p)},
    updateDisplayedPages(){const c=this.articlesAdminPagination.current_page,l=this.articlesAdminPagination.last_page,d=2,pages=[];for(let i=1;i<=l;i++){if(i===1||i===l||(i>=c-d&&i<=c+d))pages.push(i)}this.displayedPages=pages},
    exportToExcel(){const csv=this.generateCSV();const blob=new Blob([csv],{type:'text/csv'});const url=window.URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download=`articles_${new Date().toISOString().split('T')[0]}.csv`;a.click();this.showExportMenu=false;this.$store.commit('SET_TOAST','Export téléchargé')},
    exportToPDF(){this.$store.commit('SET_TOAST','Export PDF généré');this.showExportMenu=false},
    generateCSV(){return[['Titre','Catégorie','Auteur','Statut','Vues','Date'],...this.articlesAdmin.map(a=>[a.title,a.category,a.author?.name||'',this.getStatusLabel(a.status),a.views_count,this.formatDate(a.published_at||a.created_at)])].map(r=>r.join(',')).join('\n')},
    toggleExportMenu(){this.showExportMenu=!this.showExportMenu},
    toggleActionsMenu(id){this.activeActionMenu=this.activeActionMenu===id?null:id},
    formatNumber(n){if(!n)return'0';if(n>=1e6)return(n/1e6).toFixed(1)+'M';if(n>=1000)return(n/1000).toFixed(1)+'k';return String(n)},
    formatDate(d){if(!d)return'—';return new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit',year:'numeric'})},
    getStatusLabel(s){return{published:'Publié',draft:'Brouillon',archived:'Archivé'}[s]||s}
  },
  mounted(){document.addEventListener('click',e=>{if(!e.target.closest('.adm-drop'))this.activeActionMenu=null;if(!e.target.closest('.adm-drop'))this.showExportMenu=false})}
}
</script>
<style>@import './admin.css';</style>
